import React from 'react';
import { InsuranceBill, BillItem, PaymentRecord } from './types';
import { downloadPDF } from '../../../components/pdf/utils/pdfDownload';
import InsuranceBillPDFDocument from '../../../components/pdf/documents/InsuranceBillPDFDocument';
import { PDFConfig } from '../../../hooks/usePDFConfig';
import { IPatient } from '../../../constants/types';
import { stringDatePostgresToDate } from '../../../utils/index.jsx';

/**
 * Helper function to format patient name
 */
export const formatPatientName = (patient: IPatient | null): string => {
    if (!patient?.personalData) return '';
    return `${patient.personalData.name} ${patient.personalData.surnames}`;
};

/**
 * Helper function to extract patient UUID from billItem (supports both formats)
 */
export const getPatientUuid = (billItem: BillItem): string | undefined => {
    return (billItem as any).patientUUID || (billItem as any).patientUuid;
};

/**
 * Helper function to get bill identifier (idInvestigation, documentNumber, or id)
 */
export const getBillIdentifier = (bill: InsuranceBill): number | undefined => {
    return (bill as any).idInvestigation || (bill as any).documentNumber || (bill as any).id;
};

/**
 * Fetch patients for bill items
 * 
 * @param billItems - The bill items containing patient UUIDs
 * @param fetchPatientFn - Function to fetch a single patient by UUID
 * @returns Promise resolving to a map of patient UUID to patient data
 */
export const fetchPatientsForBillItems = async (
    billItems: BillItem[],
    fetchPatientFn: (uuidPatient: string) => Promise<IPatient | null>
): Promise<Record<string, IPatient>> => {
    if (!billItems || billItems.length === 0) {
        return {};
    }

    // Collect unique patient UUIDs directly from billItems
    const patientUuidsSet = new Set<string>();
    billItems.forEach((billItem) => {
        const patientUuid = getPatientUuid(billItem);
        if (patientUuid) {
            patientUuidsSet.add(patientUuid);
        }
    });

    if (patientUuidsSet.size === 0) {
        return {};
    }

    // Fetch all patients in parallel
    const patientPromises = Array.from(patientUuidsSet).map(async (patientUuid) => {
        try {
            const patient = await fetchPatientFn(patientUuid);
            return patient ? { patientUuid, patient } : null;
        } catch (error) {
            console.error(`[fetchPatientsForBillItems] Error fetching patient for UUID ${patientUuid}:`, error);
            return null;
        }
    });

    const results = await Promise.all(patientPromises);
    const patientsMap: Record<string, IPatient> = {};
    
    results.forEach((result) => {
        if (result?.patient) {
            patientsMap[result.patientUuid] = result.patient;
        }
    });

    return patientsMap;
};

/**
 * Enhance bill items with patient data from patientsMap
 * 
 * @param billItems - The bill items to enhance
 * @param patientsMap - Map of patient UUID to patient data
 * @returns Enhanced bill items with patient names, payment amounts, and bill dates
 */
export const enhanceBillItemsWithPatientData = (
    billItems: BillItem[],
    patientsMap: Record<string, IPatient>
): (BillItem & {
    patientPaymentAmount?: number;
    patientName?: string;
    patientBillCreatedAt?: Date;
    patientBillId?: string;
    patientBillUuid?: string;
})[] => {
    if (!billItems || billItems.length === 0) {
        return [];
    }

    const enhanced = billItems.map((billItem) => {
        const patientUuid = getPatientUuid(billItem);
        const patient = patientUuid ? patientsMap[patientUuid] : null;
        const patientName = formatPatientName(patient);

        // Calculate patientPaymentAmount from insuranceClaim (patientTotalBill - approvedAmount)
        const patientPaymentAmount = billItem.insuranceClaim
            ? billItem.insuranceClaim.patientTotalBill - billItem.insuranceClaim.approvedAmount
            : 0;
        
        // Extract bill data from the new structure (patientBill.createdAt, patientBill.documentNumber, patientBill.uuid)
        const patientBillCreatedAt = billItem.patientBill?.createdAt 
            ? (billItem.patientBill.createdAt instanceof Date 
                ? billItem.patientBill.createdAt 
                : stringDatePostgresToDate(billItem.patientBill.createdAt as any))
            : undefined;
        
        const patientBillId = billItem.patientBill?.documentNumber;
        const patientBillUuid = billItem.patientBill?.uuid;
        
        return {
            ...billItem,
            patientName,
            patientPaymentAmount,
            patientBillCreatedAt,
            patientBillId,
            patientBillUuid,
        };
    });
    return enhanced;
};

/**
 * Generate and download an insurance bill PDF
 * 
 * @param bill - The insurance bill to generate PDF for
 * @param billItems - Enhanced bill items with patient data
 * @param paymentRecords - Payment records associated with the bill
 * @param pdfConfig - PDF configuration from usePDFConfig hook
 * @param currency - Currency code (e.g., 'USD', 'EUR')
 * @param languageCode - Language code for localization (e.g., 'en', 'fr')
 * @returns Promise that resolves when PDF is downloaded
 */
export const generateInsuranceBillPDF = async (
    bill: InsuranceBill,
    billItems: (BillItem & {
        patientPaymentAmount?: number;
        patientName?: string;
        patientBillCreatedAt?: Date;
        patientBillId?: string;
        patientBillUuid?: string;
    })[],
    paymentRecords: PaymentRecord[],
    pdfConfig: PDFConfig,
    currency: string,
    languageCode: string
): Promise<void> => {
    const billIdentifier = getBillIdentifier(bill);
    
    // Enhance bill with patient data and identifier
    const enhancedBill = {
        ...bill,
        billItems: billItems,
        ...(billIdentifier && { idInvestigation: billIdentifier })
    };

    // Create PDF document
    const pdfDocument = (
        <InsuranceBillPDFDocument
            bill={enhancedBill}
            currency={currency}
            locale={languageCode}
            config={pdfConfig}
            paymentRecords={paymentRecords}
        />
    );

    // Generate filename with organization name and document number
    const orgName = bill.organizationName.replace(/[^a-zA-Z0-9]/g, '_');
    const docNumber = billIdentifier || 'N/A';
    const filename = `Insurance_Bill_${orgName}_${docNumber}`;

    // Download PDF
    await downloadPDF(pdfDocument, filename);
};

